import { useRef, useState } from "react";
import { Plus, X, Loader2, Sparkles, Check } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import { LANGS, useApp } from "@/lib/i18n";
import { AutoText, IconBtn } from "../official/Field";
import { ShareBar } from "./ShareBar";

function useSave(meetingId, kind, onLocal) {
  const [saved, setSaved] = useState("idle");
  const t = useRef(null);
  const update = (content) => {
    onLocal(content);
    setSaved("saving");
    clearTimeout(t.current);
    t.current = setTimeout(() => api.put(`/meetings/${meetingId}/documents/${kind}`, content).then(() => setSaved("saved")).catch((e) => { setSaved("idle"); toast.error(formatApiError(e)); }), 900);
  };
  return [saved, update];
}

export const GenericDocEditor = ({ meeting, kind, doc, setDoc, canWrite, info }) => {
  const { L } = useApp();
  const [lang, setLang] = useState(doc?.output_language || meeting.output_language || "ms");
  const [busy, setBusy] = useState(false);
  const [saved, update] = useSave(meeting.id, kind, (content) => setDoc({ ...doc, content }));
  const c = doc?.content;
  const set = (k, v) => update({ ...c, [k]: v });
  const generate = async () => {
    setBusy(true);
    try { setDoc((await api.post(`/meetings/${meeting.id}/documents/${kind}`, { output_language: lang }, { timeout: 180000 })).data); toast.success(L("Dokumen dijana", "Document generated")); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-5" data-testid={`doc-editor-${kind}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div><h3 className="font-heading text-base font-bold">{info.title}</h3><p className="mt-1 max-w-xl text-xs text-slate-500">{info.desc}</p></div>
        <div className="flex flex-wrap items-center gap-2">
          {canWrite && <Select value={lang} onValueChange={setLang}><SelectTrigger className="h-9 w-[150px] rounded-xl text-xs" data-testid="doc-lang-select"><SelectValue /></SelectTrigger><SelectContent>{LANGS.map(([k, ms, en]) => <SelectItem key={k} value={k}>{L(ms, en)}</SelectItem>)}</SelectContent></Select>}
          {canWrite && <Button size="sm" disabled={busy} onClick={generate} className="rounded-xl bg-violet-600 hover:bg-violet-700" data-testid="doc-generate-btn">{busy ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Sparkles size={14} className="mr-1" />}{busy ? L("Menjana… (~1 minit)", "Generating… (~1 min)") : c ? L("Jana semula", "Regenerate") : L("Tukar transkrip", "Convert transcript")}</Button>}
          {saved === "saved" && <span className="flex items-center gap-1 text-xs text-emerald-600" data-testid="doc-saved"><Check size={12} /> {L("Disimpan", "Saved")}</span>}
        </div>
      </div>
      {c && <div className="mt-4"><ShareBar meeting={meeting} kind={kind} /></div>}
      {c ? (
        <div className="mt-5 overflow-x-auto rounded-xl bg-slate-100 p-3 sm:p-6">
          <div className="mx-auto min-w-[600px] max-w-[820px] space-y-5 bg-white px-8 py-10 font-['Arial',sans-serif] text-[14px] text-slate-900 shadow-sm sm:px-14" data-testid="doc-paper">
            <AutoText value={c.title} bold disabled={!canWrite} onChange={(v) => set("title", v)} className="text-center text-lg uppercase" testId="doc-title" />
            <div className="space-y-0.5">
              {c.meta.map((m, i) => (
                <div key={i} className="grid grid-cols-[11rem_1rem_1fr_1.75rem] items-start">
                  <AutoText value={m.label} bold disabled={!canWrite} onChange={(v) => set("meta", c.meta.map((x, j) => (j === i ? { ...x, label: v } : x)))} testId={`doc-meta-label-${i}`} />
                  <span className="pt-0.5">:</span>
                  <AutoText value={m.value} disabled={!canWrite} onChange={(v) => set("meta", c.meta.map((x, j) => (j === i ? { ...x, value: v } : x)))} testId={`doc-meta-value-${i}`} />
                  {canWrite ? <IconBtn onClick={() => set("meta", c.meta.filter((_, j) => j !== i))} label={L("Buang", "Remove")} testId={`doc-meta-remove-${i}`}><X size={14} /></IconBtn> : <span />}
                </div>
              ))}
              {canWrite && <button onClick={() => set("meta", [...c.meta, { label: "", value: "" }])} className="flex items-center gap-1 text-xs font-semibold text-violet-700 hover:underline" data-testid="doc-meta-add"><Plus size={12} /> {L("Tambah maklumat", "Add detail")}</button>}
            </div>
            {c.sections.map((s, i) => (
              <div key={i} data-testid={`doc-section-${i}`}>
                <div className="flex items-start gap-2">
                  <AutoText value={s.heading} bold disabled={!canWrite} onChange={(v) => set("sections", c.sections.map((x, j) => (j === i ? { ...x, heading: v } : x)))} className="text-[15px]" testId={`doc-section-heading-${i}`} />
                  {canWrite && <IconBtn onClick={() => set("sections", c.sections.filter((_, j) => j !== i))} label={L("Buang bahagian", "Remove section")} testId={`doc-section-remove-${i}`}><X size={14} /></IconBtn>}
                </div>
                <AutoText value={s.body} disabled={!canWrite} onChange={(v) => set("sections", c.sections.map((x, j) => (j === i ? { ...x, body: v } : x)))} className="text-justify" testId={`doc-section-body-${i}`} />
              </div>
            ))}
            {canWrite && <button onClick={() => set("sections", [...c.sections, { heading: "", body: "" }])} className="flex items-center gap-1 text-xs font-semibold text-violet-700 hover:underline" data-testid="doc-section-add"><Plus size={12} /> {L("Tambah bahagian", "Add section")}</button>}
            {canWrite && <p className="text-[11px] text-slate-400">{L("Petua: mulakan baris dengan “- ” untuk senarai bertitik.", "Tip: start a line with “- ” for a bullet.")}</p>}
          </div>
        </div>
      ) : (
        <div className="mt-5 grid h-40 place-items-center rounded-xl border border-dashed border-slate-200 bg-slate-50 text-sm text-slate-400" data-testid="doc-empty">{L("Tekan “Tukar transkrip” untuk menjana dokumen ini.", "Press “Convert transcript” to generate this document.")}</div>
      )}
    </section>
  );
};
